import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import FilterTextField from "components/FilterTextField";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import GradientLineChart from "examples/Charts/LineCharts/GradientLineChart";
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";
import HorizontalBarChart from "examples/Charts/BarCharts/HorizontalBarChart";
import DefaultDoughnutChart from "examples/Charts/DoughnutCharts/DefaultDoughnutChart";

import DashboardMapWidget from "layouts/dashboard/DashboardMapWidget";
import api from "services/api";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Loading from "components/Loading";
import RecruiterTeachersDialog from "components/RecruiterTeachersDialog";
import { useTranslate } from "utils/i18n";
import { apiurl } from "utils";
import {
  defaultTrendRange,
  formatDateParam,
  buildRegionalChart,
  trendRangeFromPreset,
} from "layouts/dashboard/dashboardChartHelpers";
import { figmaTokens } from "assets/theme/designTokens";
import { extractLeaveListStats } from "utils/paginationStats";
import { useSoftUIController } from "context";
import { isObserver } from "assistant/userRole";

const withTrailingSlash = (value) => (value.endsWith("/") ? value : `${value}/`);
const leaveApiBase = withTrailingSlash(process.env.REACT_APP_LEAVE_API_BASE || apiurl);
const leaveUpdateUrl = `${leaveApiBase}LeaveRequests/Update`;
const LEAVE_STATUS_OPTIONS = ["Pending", "Approved", "Rejected", "Cancelled"];
const SHOW_LICENSE_DASHBOARD_SECTIONS = false;
const SHOW_RECRUITER_PREVIEW = false;

const statCardWrapSx = {
  minWidth: 0,
  height: "100%",
  display: "flex",
  "& > .MuiCard-root": {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 112,
    borderRadius: "14px",
    border: `1px solid ${figmaTokens.borderSubtle}`,
    borderLeft: `3px solid rgba(102, 126, 234, 0.42)`,
    boxShadow: "0 8px 28px rgba(15, 23, 42, 0.06)",
    backgroundColor: figmaTokens.surface,
    backgroundImage: `linear-gradient(135deg, rgba(102, 126, 234, 0.07) 0%, ${figmaTokens.surface} 42%)`,
    overflow: "hidden",
    transition: "box-shadow 0.22s ease, transform 0.22s ease, border-color 0.22s ease",
    "&:hover": {
      borderColor: "rgba(102, 126, 234, 0.28)",
      borderLeftColor: figmaTokens.accent,
      boxShadow: "0 14px 36px rgba(102, 126, 234, 0.12)",
      transform: "translateY(-2px)",
    },
    "& > div": {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    },
  },
};

const previewShellSx = {
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  border: "none",
  borderRadius: "18px",
  overflow: "hidden",
  boxShadow: "0 22px 56px rgba(15, 23, 42, 0.1)",
};

const previewAccentBarSxRecruiters = {
  height: 5,
  flexShrink: 0,
  background: figmaTokens.gradientCSS,
};

const previewAccentBarSxLeaves = {
  height: 5,
  flexShrink: 0,
  background: figmaTokens.gradientDiagonalCSS,
};

function formatLeaveDate(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "—" : format(parsed, "yyyy-MM-dd");
}

const RADIUS_PRESETS = [
  { m: 500, labelKey: "dashboard.filters.radius_preset_500m", fallback: "500 m" },
  { m: 5000, labelKey: "dashboard.filters.radius_preset_5km", fallback: "5 km" },
  { m: 50000, labelKey: "dashboard.filters.radius_preset_50km", fallback: "50 km" },
  { m: 500000, labelKey: "dashboard.filters.radius_preset_500km", fallback: "500 km" },
];

function Dashboard() {
  const [controller] = useSoftUIController();
  const observerView = isObserver(controller.user);
  const [loading, setLoading] = useState(false);
  const initialRange = defaultTrendRange();
  const [trendStart, setTrendStart] = useState(initialRange.start);
  const [trendEnd, setTrendEnd] = useState(initialRange.end);
  /** When set, matches pill selection for trend/participation charts (Daily + both participation lines). */
  const [trendRangePreset, setTrendRangePreset] = useState(null);
  const [regionalDate, setRegionalDate] = useState(formatDateParam(new Date()));
  const [radiusMeters, setRadiusMeters] = useState(500000);

  const [dashboardStats, setDashboardStats] = useState({
    licensedTeachers: 0,
    maxLicensedTeachers: 0,
    totalTeachers: 0,
    remainingLicenses: 0,
  });
  const [regionalSummary, setRegionalSummary] = useState({
    reportedSum: 0,
    withinRadiusSum: 0,
    rosterTotal: 0,
  });
  const [areaAttendance, setAreaAttendance] = useState({
    teachersAssigned: 0,
    teachersChecked: 0,
    checkInRate: 0,
  });

  const [dailyBarChart, setDailyBarChart] = useState({ labels: [], datasets: [] });
  const [teacherParticipationChart, setTeacherParticipationChart] = useState({ labels: [], datasets: [] });
  const [establishmentParticipationChart, setEstablishmentParticipationChart] = useState({
    labels: [],
    datasets: [],
  });
  const [provinceChart, setProvinceChart] = useState({ labels: [], datasets: [] });
  const [departmentChart, setDepartmentChart] = useState({ labels: [], datasets: [] });
  const [recruitersList, setRecruitersList] = useState([]);
  const [leaveRequestsList, setLeaveRequestsList] = useState([]);
  const [leaveListStats, setLeaveListStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    cancelled: 0,
  });
  const [leaveUpdatingId, setLeaveUpdatingId] = useState(null);
  const [recruiterTeachersOpen, setRecruiterTeachersOpen] = useState(false);
  const [recruiterTeachersRow, setRecruiterTeachersRow] = useState(null);

  const { t } = useTranslate();
  const recruitersPreview = useMemo(() => recruitersList.slice(0, 5), [recruitersList]);

  const pendingLeaveCount = leaveListStats.pending;

  const leavePreview = useMemo(() => {
    const list = [...leaveRequestsList];
    const pending = list.filter((row) => String(row?.status ?? "Pending") === "Pending");
    const other = list.filter((row) => String(row?.status ?? "Pending") !== "Pending");
    return [...pending, ...other].slice(0, 5);
  }, [leaveRequestsList]);

  const licenseUtilizationPct = useMemo(() => {
    const cap = Math.max(0, Number(dashboardStats.maxLicensedTeachers) || 0);
    const licensed = Math.max(0, Number(dashboardStats.licensedTeachers) || 0);
    if (cap <= 0) return licensed > 0 ? 100 : 0;
    return Math.min(100, Math.round((licensed / cap) * 1000) / 10);
  }, [dashboardStats.licensedTeachers, dashboardStats.maxLicensedTeachers]);

  const teacherQuotaChart = useMemo(() => {
    const maxLicensed = Math.max(0, Number(dashboardStats.maxLicensedTeachers) || 0);
    const licensed = Math.max(0, Number(dashboardStats.licensedTeachers) || 0);
    const remaining = Math.max(0, Number(dashboardStats.remainingLicenses) || 0);

    if (maxLicensed <= 0 && licensed <= 0) {
      return {
        labels: [t("dashboard.chart.quota_empty", "No license data")],
        datasets: {
          label: t("dashboard.chart.quota_label_short", "Licenses"),
          data: [1],
          backgroundColors: [figmaTokens.rosterChartMuted],
        },
        cutout: 62,
      };
    }
    if (maxLicensed <= 0) {
      return {
        labels: [t("dashboard.chart.quota_licensed", "Licensed")],
        datasets: {
          label: t("dashboard.chart.quota_label_short", "Licenses"),
          data: [licensed],
          backgroundColors: [figmaTokens.rosterChartFilled],
        },
        cutout: 62,
      };
    }
    return {
      labels: [
        t("dashboard.chart.quota_licensed", "Licensed"),
        t("dashboard.chart.quota_remaining", "Remaining licenses"),
      ],
      datasets: {
        label: t("dashboard.chart.quota_ring_label", "License usage"),
        data: [licensed, remaining],
        backgroundColors: [figmaTokens.rosterChartFilled, figmaTokens.rosterChartOpen],
      },
      cutout: 62,
    };
  }, [dashboardStats, t]);

  const applyChartsFromSummary = useCallback(
    (data) => {
      const daily = Array.isArray(data?.dailyAttendance) ? data.dailyAttendance : [];
      const labels = daily.map((row) => String(row?.attendanceDate ?? "").slice(0, 10));
      const teacherPct = daily.map((row) => {
        const total = Number(row?.totalTeachers) || 0;
        return total > 0 ? ((Number(row?.teachersReported) || 0) / total) * 100 : 0;
      });
      const establishmentPct = daily.map((row) => {
        const total = Number(row?.totalEstablishments) || 0;
        return total > 0 ? ((Number(row?.establishmentsReported) || 0) / total) * 100 : 0;
      });

      setDailyBarChart({
        labels,
        datasets: [
          {
            label: t("dashboard.chart.checked_in_pct", "Checked in"),
            color: "info",
            data: teacherPct.map((value) => Math.round(value * 10) / 10),
          },
          {
            label: t("dashboard.chart.not_checked_in_pct", "Not checked in"),
            color: "dark",
            data: teacherPct.map(
              (value) => Math.round(Math.max(0, 100 - value) * 10) / 10
            ),
          },
        ],
      });

      setTeacherParticipationChart({
        labels,
        datasets: [
          {
            label: t("dashboard.chart.teacher_ratio", "Teacher ratio (day / roster)"),
            color: "info",
            data: teacherPct,
            gradientFillOpacity: 0.52,
          },
        ],
      });
      setEstablishmentParticipationChart({
        labels,
        datasets: [
          {
            label: t("dashboard.chart.establishment_ratio", "Establishment ratio (day / roster)"),
            color: "secondary",
            data: establishmentPct,
            gradientFillOpacity: 0.48,
          },
        ],
      });
    },
    [t]
  );

  const fetchDashboardSummary = useCallback(
    async (overrides = {}) => {
      const start = formatDateParam(overrides.trendStart ?? trendStart);
      const end = formatDateParam(overrides.trendEnd ?? trendEnd);
      const snapshotDate = formatDateParam(overrides.snapshotDate ?? regionalDate);
      const requestedRadius = Number(overrides.radius ?? radiusMeters);
      const radius = Math.max(1, Math.min(500000, Math.round(requestedRadius || 500)));

      try {
        const { data } = await api.get("Dashboard/summary", {
          params: { trendStart: start, trendEnd: end, snapshotDate, radius },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("tk446655440000")}`,
          },
        });

        const overview = data?.overview ?? {};
        const licenses = data?.licenseSummary;
        setDashboardStats({
          licensedTeachers: Number(licenses?.licensedTeachers) || 0,
          maxLicensedTeachers: Number(licenses?.maximumLicenses) || 0,
          totalTeachers: Number(overview?.totalTeachers) || 0,
          remainingLicenses: Number(licenses?.remainingLicenses) || 0,
        });
        setRegionalSummary({
          reportedSum: Number(overview?.teachersReported) || 0,
          withinRadiusSum: Number(overview?.teachersWithinRadius) || 0,
          rosterTotal: Number(overview?.totalTeachers) || 0,
        });
        setAreaAttendance({
          teachersAssigned: Number(data?.areaAttendance?.teachersAssigned) || 0,
          teachersChecked: Number(data?.areaAttendance?.teachersChecked) || 0,
          checkInRate: Number(data?.areaAttendance?.checkInRate) || 0,
        });
        applyChartsFromSummary(data);

        const provinceRows = (data?.provinceStatistics ?? []).map((row) => ({
          ...row,
          categoryType: "Province",
        }));
        const departmentRows = (data?.departmentStatistics ?? []).map((row) => ({
          ...row,
          categoryType: "Department",
        }));
        setProvinceChart(buildRegionalChart(provinceRows, "Province", t));
        setDepartmentChart(buildRegionalChart(departmentRows, "Department", t));

        const adminWidgets = data?.adminWidgets;
        const recruiters = Array.isArray(adminWidgets?.recentRecruiters)
          ? adminWidgets.recentRecruiters
          : [];
        const leaves = Array.isArray(adminWidgets?.recentLeaveRequests)
          ? adminWidgets.recentLeaveRequests
          : [];
        setRecruitersList(recruiters);
        setLeaveRequestsList(leaves);
        setLeaveListStats({
          ...extractLeaveListStats(leaves),
          pending: Number(adminWidgets?.pendingLeaveCount) || 0,
        });
      } catch (err) {
        console.error("[Dashboard summary] request failed", {
          status: err?.response?.status,
          data: err?.response?.data,
          message: err?.message,
        });
      }
    },
    [trendStart, trendEnd, regionalDate, radiusMeters, applyChartsFromSummary, t]
  );

  const handleLeaveStatusChange = useCallback(
    async (row, nextStatus) => {
      const id = row?.id ?? row?.Id;
      if (id == null || nextStatus == null) return;
      setLeaveUpdatingId(id);
      try {
        const token = localStorage.getItem("tk446655440000");
        const formData = new FormData();
        formData.append("Id", id);
        formData.append("Status", nextStatus);
        await api.post(leaveUpdateUrl, formData, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            "Content-Type": "multipart/form-data",
          },
        });
        await fetchDashboardSummary();
      } catch (err) {
        console.error("[Dashboard leave status update] failed", err);
      } finally {
        setLeaveUpdatingId(null);
      }
    },
    [fetchDashboardSummary]
  );

  const fetchTrendStats = useCallback(
    async (rangeOverride) =>
      fetchDashboardSummary({
        trendStart: rangeOverride?.start ?? trendStart,
        trendEnd: rangeOverride?.end ?? trendEnd,
      }),
    [fetchDashboardSummary, trendStart, trendEnd]
  );

  const handleTrendRangePreset = useCallback(
    (_event, value) => {
      if (value == null) return;
      setTrendRangePreset(value);
      const range = trendRangeFromPreset(value);
      setTrendStart(range.start);
      setTrendEnd(range.end);
      fetchTrendStats({ start: range.start, end: range.end });
    },
    [fetchTrendStats]
  );

  const fetchRegionalStats = useCallback(
    async () => fetchDashboardSummary({ snapshotDate: regionalDate, radius: radiusMeters }),
    [fetchDashboardSummary, regionalDate, radiusMeters]
  );

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      await fetchDashboardSummary();
    } finally {
      setLoading(false);
    }
  }, [fetchDashboardSummary]);

  useEffect(() => {
    loadAll();
    // Initial load only; filters refresh via buttons.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only bootstrap
  }, []);

  return (
    <DashboardLayout>
      {loading && <Loading />}
      <DashboardNavbar />
      <SoftBox py={3}>
        <Card
          sx={{
            mb: 2.5,
            borderRadius: "18px",
            border: `1px solid ${figmaTokens.borderSubtle}`,
            boxShadow: "0 14px 44px rgba(15, 23, 42, 0.07)",
            overflow: "hidden",
          }}
        >
          <SoftBox sx={{ height: 4, flexShrink: 0, background: figmaTokens.gradientCSS }} />
          <SoftBox sx={{ p: { xs: 2, md: 2.25 } }}>
            <SoftBox
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
                mb: 2,
              }}
            >
              <SoftBox sx={{ minWidth: 0, flex: "1 1 200px" }}>
                <SoftTypography variant="subtitle1" fontWeight="bold" component="div">
                  {t("dashboard.filters.panel_title", "Reporting filters")}
                </SoftTypography>
                <SoftTypography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, lineHeight: 1.45 }}>
                  {t(
                    "dashboard.filters.panel_hint_short",
                    "Trend dates drive the charts below; regional date + radius drive the map, two regional charts, and two attendance KPI cards."
                  )}
                </SoftTypography>
              </SoftBox>
              <Tooltip
                title={t(
                  "dashboard.filters.tooltip_refresh_all",
                  "Runs trend, regional, recruiter, and leave requests so KPIs and previews stay in sync."
                )}
                arrow
                placement="left"
              >
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={loadAll}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      px: 2.25,
                      py: 0.65,
                      borderRadius: 2,
                      color: "#fff",
                      background: figmaTokens.gradientCSS,
                      boxShadow: "0 6px 18px rgba(102, 126, 234, 0.38)",
                      "&:hover": {
                        filter: "brightness(1.06)",
                        boxShadow: "0 8px 22px rgba(102, 126, 234, 0.42)",
                      },
                      "&.Mui-disabled": {
                        background: figmaTokens.gradientCSS,
                        opacity: 0.55,
                        color: "#fff",
                      },
                    }}
                  >
                    {t("dashboard.filters.refresh_all", "Refresh all")}
                  </Button>
                </span>
              </Tooltip>
            </SoftBox>

            <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={12} lg={6} sx={{ display: "flex" }}>
                <SoftBox
                  sx={{
                    p: 1.75,
                    pt: 2,
                    pb: 1.5,
                    borderRadius: "14px",
                    border: `1px solid ${figmaTokens.borderSubtle}`,
                    bgcolor: figmaTokens.pageBg,
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.25 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                      <Icon sx={{ color: figmaTokens.accent, fontSize: 22 }}>date_range</Icon>
                      <SoftTypography variant="subtitle2" fontWeight={700} sx={{ color: figmaTokens.textPrimary }}>
                        {t("dashboard.filters.trend_title", "Trend period")}
                      </SoftTypography>
                    </Stack>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => fetchTrendStats()}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: "10px",
                        borderColor: figmaTokens.borderStrong,
                        color: figmaTokens.textPrimary,
                        flexShrink: 0,
                      }}
                    >
                      {t("dashboard.filters.apply_trends", "Apply range")}
                    </Button>
                  </Stack>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <FilterTextField
                        type="date"
                        label={t("dashboard.filters.start_date", "From")}
                        value={trendStart}
                        onChange={(e) => {
                          setTrendStart(e.target.value);
                          setTrendRangePreset(null);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FilterTextField
                        type="date"
                        label={t("dashboard.filters.end_date", "To")}
                        value={trendEnd}
                        onChange={(e) => {
                          setTrendEnd(e.target.value);
                          setTrendRangePreset(null);
                        }}
                      />
                    </Grid>
                  </Grid>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mt: 1.25, minHeight: 32, flexWrap: "wrap", gap: 0.5 }}
                  >
                    <SoftTypography variant="caption" sx={{ color: figmaTokens.textSecondary, fontWeight: 600 }}>
                      {t("dashboard.filters.trend_range_hint", "Daily charts use this range after you click Apply.")}
                    </SoftTypography>
                  </Stack>
                </SoftBox>
              </Grid>
              <Grid item xs={12} lg={6} sx={{ display: "flex" }}>
                <SoftBox
                  sx={{
                    p: 1.75,
                    pt: 2,
                    pb: 1.5,
                    borderRadius: "14px",
                    border: `1px solid ${figmaTokens.borderSubtle}`,
                    bgcolor: figmaTokens.pageBg,
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.25 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                      <Icon sx={{ color: figmaTokens.accentEnd, fontSize: 22 }}>radar</Icon>
                      <SoftTypography variant="subtitle2" fontWeight={700} sx={{ color: figmaTokens.textPrimary }}>
                        {t("dashboard.filters.regional_title", "Regional snapshot (date & radius)")}
                      </SoftTypography>
                    </Stack>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => fetchRegionalStats()}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: "10px",
                        borderColor: figmaTokens.borderStrong,
                        color: figmaTokens.textPrimary,
                        flexShrink: 0,
                      }}
                    >
                      {t("dashboard.filters.apply_regional", "Apply snapshot")}
                    </Button>
                  </Stack>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <FilterTextField
                        type="date"
                        label={t("dashboard.filters.snapshot_date", "Snapshot date")}
                        value={regionalDate}
                        onChange={(e) => setRegionalDate(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FilterTextField
                        type="number"
                        label={t("dashboard.filters.radius_m", "Distance (meters)")}
                        value={radiusMeters}
                        onChange={(e) =>
                          setRadiusMeters(e.target.value === "" ? 0 : Number(e.target.value))
                        }
                        inputProps={{ min: 0, step: 100 }}
                      />
                    </Grid>
                  </Grid>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mt: 1.25, gap: 1, flexWrap: "wrap", minHeight: 32 }}
                  >
                    <SoftTypography variant="caption" sx={{ color: figmaTokens.textSecondary, fontWeight: 700 }}>
                      {t("dashboard.filters.radius_quick_label", "Quick radius")}
                    </SoftTypography>
                    <SoftBox sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, justifyContent: "flex-end", flex: "1 1 auto" }}>
                      {RADIUS_PRESETS.map((p) => (
                        <Chip
                          key={p.m}
                          label={t(p.labelKey, p.fallback)}
                          size="small"
                          onClick={() => setRadiusMeters(p.m)}
                          color={Number(radiusMeters) === p.m ? "primary" : "default"}
                          variant={Number(radiusMeters) === p.m ? "filled" : "outlined"}
                          sx={{ fontWeight: 600 }}
                        />
                      ))}
                    </SoftBox>
                  </Stack>
                </SoftBox>
              </Grid>
            </Grid>
          </SoftBox>
        </Card>

        <SoftBox
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              sm: "repeat(2, minmax(0, 1fr))",
              md: `repeat(${observerView ? 2 : 3}, minmax(0, 1fr))`,
              lg: `repeat(${observerView ? 2 : 3}, minmax(0, 1fr))`,
            },
            mb: 3,
          }}
        >
          {SHOW_LICENSE_DASHBOARD_SECTIONS && !observerView && <SoftBox sx={statCardWrapSx}>
            <MiniStatisticsCard
              title={{ text: t("dashboard.stats.licensed_teachers", "Licensed") }}
              count={dashboardStats.licensedTeachers}
              percentage={{
                color: "info",
                text:
                  dashboardStats.maxLicensedTeachers > 0
                    ? `/ ${dashboardStats.maxLicensedTeachers}`
                    : "",
              }}
              icon={{ color: "success", component: "verified" }}
            />
          </SoftBox>}
          {SHOW_LICENSE_DASHBOARD_SECTIONS && <SoftBox sx={statCardWrapSx}>
            <MiniStatisticsCard
              title={{ text: t("dashboard.stats.registered_teachers", "Registered teachers") }}
              count={dashboardStats.totalTeachers}
              percentage={{ color: "info", text: "" }}
              icon={{ color: "info", component: "groups" }}
            />
          </SoftBox>}
          {!observerView && <SoftBox sx={statCardWrapSx}>
            <MiniStatisticsCard
              title={{ text: t("dashboard.stats.leave_pending", "Pending leave requests") }}
              count={pendingLeaveCount}
              percentage={{ color: "warning", text: "" }}
              icon={{ color: "warning", component: "pending_actions" }}
            />
          </SoftBox>}
          <SoftBox sx={statCardWrapSx}>
            <MiniStatisticsCard
              title={{ text: t("dashboard.stats.people_checked_in", "People checked in") }}
              count={regionalSummary.reportedSum}
              percentage={{ color: "success", text: "" }}
              icon={{ color: "secondary", component: "assignment_turned_in" }}
            />
          </SoftBox>
          <SoftBox sx={statCardWrapSx}>
            <MiniStatisticsCard
              title={{ text: t("dashboard.stats.within_radius_total", "Within radius (total)") }}
              count={regionalSummary.withinRadiusSum}
              percentage={{ color: "success", text: "" }}
              icon={{
                color: "success",
                component: "radar",
              }}
            />
          </SoftBox>
        </SoftBox>

        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <VerticalBarChart
                title={t("dashboard.chart.attendance_percentage", "Attendance percentage")}
                description={t(
                  "dashboard.chart.attendance_percentage_hint",
                  "Daily percentage of people who checked in and did not check in."
                )}
                height="22rem"
                chart={dailyBarChart}
                showLegend
                headerActions={
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={trendRangePreset}
                    onChange={handleTrendRangePreset}
                    aria-label={t("dashboard.chart.range_presets_aria", "Presets: 1 month to 1 year")}
                    sx={{
                      flexWrap: "wrap",
                      justifyContent: "flex-end",
                      maxWidth: { xs: "100%", sm: 420 },
                      "& .MuiToggleButton-root": {
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.7rem",
                        px: 1.25,
                        py: 0.5,
                        border: `1px solid ${figmaTokens.borderSubtle} !important`,
                        borderRadius: "8px !important",
                      },
                    }}
                  >
                    <ToggleButton value="1m" aria-label="1 month">
                      {t("dashboard.chart.range_1m", "1 mo")}
                    </ToggleButton>
                    <ToggleButton value="3m" aria-label="3 months">
                      {t("dashboard.chart.range_3m", "3 mo")}
                    </ToggleButton>
                    <ToggleButton value="6m" aria-label="6 months">
                      {t("dashboard.chart.range_6m", "6 mo")}
                    </ToggleButton>
                    <ToggleButton value="1y" aria-label="1 year">
                      {t("dashboard.chart.range_1y", "1 yr")}
                    </ToggleButton>
                  </ToggleButtonGroup>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <GradientLineChart
                    title={t(
                      "dashboard.chart.participation_teachers_title",
                      "Teacher participation (% of roster)"
                    )}
                    description={t(
                      "dashboard.chart.participation_teachers_hint",
                      "Daily teacher reporting as a share of the roster."
                    )}
                    height="20rem"
                    chart={teacherParticipationChart}
                    showLegend={false}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <GradientLineChart
                    title={t(
                      "dashboard.chart.participation_establishment_title",
                      "Establishment participation (% of roster)"
                    )}
                    description={t(
                      "dashboard.chart.participation_establishment_hint",
                      "Daily establishment reporting as a share of the roster."
                    )}
                    height="20rem"
                    chart={establishmentParticipationChart}
                    showLegend={false}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </SoftBox>

        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <DashboardMapWidget
                areaAttendance={areaAttendance}
                loading={loading}
                showOpenButton={!observerView}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <HorizontalBarChart
                title={t("dashboard.regional.provinces", "By province")}
                height="18rem"
                chart={provinceChart}
                showLegend
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <HorizontalBarChart
                title={t("dashboard.regional.departments", "By department")}
                height="18rem"
                chart={departmentChart}
                showLegend
              />
            </Grid>
          </Grid>
        </SoftBox>

        {!observerView && <SoftBox mb={3}>
          <Grid container spacing={3} alignItems="stretch">
            {SHOW_RECRUITER_PREVIEW && <Grid item xs={12} md={6} sx={{ display: "flex", minWidth: 0 }}>
              <Card
                sx={{
                  ...previewShellSx,
                  background: `linear-gradient(165deg, rgba(102, 126, 234, 0.12) 0%, ${figmaTokens.surface} 52%)`,
                }}
              >
                <SoftBox sx={previewAccentBarSxRecruiters} />
                <SoftBox
                  sx={{
                    p: { xs: 2, md: 2.75 },
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <SoftBox
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 2,
                      flexWrap: "nowrap",
                      width: "100%",
                    }}
                  >
                    <SoftBox
                      sx={{
                        display: "flex",
                        gap: 2,
                        minWidth: 0,
                        alignItems: "flex-start",
                        flex: "1 1 auto",
                        pr: { xs: 0.5, sm: 1 },
                      }}
                    >
                      <SoftBox
                        sx={{
                          width: 54,
                          height: 54,
                          borderRadius: "16px",
                          background: figmaTokens.gradientCSS,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          boxShadow: "0 14px 32px rgba(102, 126, 234, 0.38)",
                        }}
                      >
                        <Icon sx={{ color: "#fff", fontSize: 28 }}>groups</Icon>
                      </SoftBox>
                      <SoftBox sx={{ minWidth: 0 }}>
                        <SoftTypography
                          variant="overline"
                          sx={{
                            letterSpacing: "0.14em",
                            fontWeight: 800,
                            color: figmaTokens.textMuted,
                            fontSize: "0.65rem",
                          }}
                        >
                          {t("dashboard.recruiters.preview_kicker", "RECRUITMENT")}
                        </SoftTypography>
                        <SoftTypography variant="h5" fontWeight="bold" sx={{ color: figmaTokens.textPrimary, mt: 0.25 }}>
                          {t("dashboard.recruiters.preview_title", "Recruiters")}
                        </SoftTypography>
                        <SoftTypography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.45 }}>
                          {t("dashboard.recruiters.count_subtitle", "{n} in your directory").replace(
                            "{n}",
                            String(recruitersList.length)
                          )}
                        </SoftTypography>
                      </SoftBox>
                    </SoftBox>
                    <Button
                      component={Link}
                      to="/recruiters"
                      variant="contained"
                      disableElevation
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        px: 2.25,
                        py: 1,
                        borderRadius: "12px",
                        color: "#fff",
                        background: figmaTokens.gradientCSS,
                        flexShrink: 0,
                        alignSelf: "flex-start",
                        ml: "auto",
                        boxShadow: "0 10px 26px rgba(102, 126, 234, 0.35)",
                        "&:hover": {
                          filter: "brightness(1.06)",
                          boxShadow: "0 14px 32px rgba(102, 126, 234, 0.42)",
                        },
                      }}
                    >
                      {t("dashboard.recruiters.view_all", "Open recruiters")}
                    </Button>
                  </SoftBox>

                  {recruitersPreview.length === 0 ? (
                    <SoftTypography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      {t("dashboard.recruiters.none", "No recruiters yet — add one from the recruiters page.")}
                    </SoftTypography>
                  ) : (
                    <SoftBox component="ul" sx={{ listStyle: "none", p: 0, m: 0, flex: 1 }}>
                      {recruitersPreview.map((row, index) => (
                        <SoftBox
                          component="li"
                          key={row.id != null ? `r-${row.id}` : `r-i-${index}`}
                          sx={{
                            py: 1.35,
                            px: 1.6,
                            mb: 1,
                            borderRadius: "14px",
                            border: "1px solid rgba(102, 126, 234, 0.18)",
                            borderLeft: "4px solid #667eea",
                            background:
                              "linear-gradient(95deg, rgba(102, 126, 234, 0.09) 0%, rgba(255, 255, 255, 0.95) 38%)",
                            transition: "transform 0.2s ease, box-shadow 0.2s ease",
                            "&:hover": {
                              transform: "translateX(5px)",
                              boxShadow: "0 10px 28px rgba(102, 126, 234, 0.14)",
                            },
                            "&:last-of-type": { mb: 0 },
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <SoftBox sx={{ flex: 1, minWidth: 0 }}>
                            <SoftTypography variant="subtitle2" fontWeight="bold" display="block" sx={{ color: figmaTokens.textPrimary }}>
                              {row.name || "—"}
                            </SoftTypography>
                            <SoftTypography variant="caption" sx={{ color: figmaTokens.textSecondary, mt: 0.35, display: "block" }}>
                              {[row.phoneNumber, row.userName].filter(Boolean).join(" · ") || "—"}
                            </SoftTypography>
                          </SoftBox>
                          <Tooltip
                            title={t("dashboard.recruiters.open_team", "View teachers enrolled by this recruiter")}
                            arrow
                          >
                            <IconButton
                              size="small"
                              aria-label={t("dashboard.recruiters.open_team", "View teachers enrolled by this recruiter")}
                              onClick={() => {
                                setRecruiterTeachersRow(row);
                                setRecruiterTeachersOpen(true);
                              }}
                              sx={{
                                flexShrink: 0,
                                color: figmaTokens.accent,
                                border: `1px solid rgba(102, 126, 234, 0.35)`,
                                bgcolor: "rgba(102, 126, 234, 0.06)",
                                "&:hover": {
                                  bgcolor: "rgba(102, 126, 234, 0.14)",
                                  borderColor: "rgba(102, 126, 234, 0.55)",
                                },
                              }}
                            >
                              <Icon fontSize="small">groups</Icon>
                            </IconButton>
                          </Tooltip>
                        </SoftBox>
                      ))}
                    </SoftBox>
                  )}
                </SoftBox>
              </Card>
            </Grid>}

            <Grid item xs={12} md={SHOW_RECRUITER_PREVIEW ? 6 : 12} sx={{ display: "flex", minWidth: 0 }}>
              <Card
                sx={{
                  ...previewShellSx,
                  background: `linear-gradient(175deg, rgba(118, 75, 162, 0.11) 0%, ${figmaTokens.surface} 55%)`,
                }}
              >
                <SoftBox sx={previewAccentBarSxLeaves} />
                <SoftBox
                  sx={{
                    p: { xs: 2, md: 2.75 },
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <SoftBox
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 2,
                      flexWrap: "nowrap",
                      width: "100%",
                    }}
                  >
                    <SoftBox
                      sx={{
                        display: "flex",
                        gap: 2,
                        minWidth: 0,
                        alignItems: "flex-start",
                        flex: "1 1 auto",
                        pr: { xs: 0.5, sm: 1 },
                      }}
                    >
                      <SoftBox
                        sx={{
                          width: 54,
                          height: 54,
                          borderRadius: "16px",
                          background: figmaTokens.gradientDiagonalCSS,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          boxShadow: "0 14px 32px rgba(118, 75, 162, 0.35)",
                        }}
                      >
                        <Icon sx={{ color: "#fff", fontSize: 28 }}>event_available</Icon>
                      </SoftBox>
                      <SoftBox sx={{ minWidth: 0 }}>
                        <SoftTypography
                          variant="overline"
                          sx={{
                            letterSpacing: "0.14em",
                            fontWeight: 800,
                            color: figmaTokens.textMuted,
                            fontSize: "0.65rem",
                          }}
                        >
                          {t("dashboard.leaves.preview_kicker", "TIME OFF")}
                        </SoftTypography>
                        <SoftTypography variant="h5" fontWeight="bold" sx={{ color: figmaTokens.textPrimary, mt: 0.25 }}>
                          {t("dashboard.leaves.preview_title", "Leave requests")}
                        </SoftTypography>
                        <SoftTypography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.45 }}>
                          {t(
                            "dashboard.leaves.preview_hint",
                            "Pending items first. Change status below or open the full page."
                          )}
                        </SoftTypography>
                      </SoftBox>
                    </SoftBox>
                    <Button
                      component={Link}
                      to="/leave-requests"
                      variant="contained"
                      disableElevation
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        px: 2.25,
                        py: 1,
                        borderRadius: "12px",
                        color: "#fff",
                        background: figmaTokens.gradientDiagonalCSS,
                        flexShrink: 0,
                        alignSelf: "flex-start",
                        ml: "auto",
                        boxShadow: "0 10px 26px rgba(118, 75, 162, 0.33)",
                        "&:hover": {
                          filter: "brightness(1.06)",
                          boxShadow: "0 14px 32px rgba(118, 75, 162, 0.4)",
                        },
                      }}
                    >
                      {t("dashboard.leaves.view_all", "Open leave requests")}
                    </Button>
                  </SoftBox>

                  {leavePreview.length === 0 ? (
                    <SoftTypography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      {t("dashboard.leaves.none", "No leave requests loaded.")}
                    </SoftTypography>
                  ) : (
                    <SoftBox component="ul" sx={{ listStyle: "none", p: 0, m: 0, flex: 1 }}>
                      {leavePreview.map((row, index) => (
                        <SoftBox
                          component="li"
                          key={row.id != null ? `l-${row.id}` : `l-i-${index}`}
                          sx={{
                            py: 1.35,
                            px: 1.6,
                            mb: 1,
                            borderRadius: "14px",
                            border: "1px solid rgba(118, 75, 162, 0.2)",
                            borderLeft: "4px solid #764ba2",
                            background:
                              "linear-gradient(95deg, rgba(118, 75, 162, 0.08) 0%, rgba(255, 255, 255, 0.96) 42%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1.25,
                            flexWrap: "wrap",
                            transition: "transform 0.2s ease, box-shadow 0.2s ease",
                            "&:hover": {
                              transform: "translateX(5px)",
                              boxShadow: "0 10px 28px rgba(118, 75, 162, 0.15)",
                            },
                            "&:last-of-type": { mb: 0 },
                          }}
                        >
                          <SoftBox sx={{ minWidth: 0, flex: "1 1 auto" }}>
                            <SoftTypography variant="subtitle2" fontWeight="bold" display="block" sx={{ color: figmaTokens.textPrimary }}>
                              {row.teacherName || "—"}
                            </SoftTypography>
                            <SoftTypography variant="caption" sx={{ color: figmaTokens.textSecondary, mt: 0.35, display: "block" }}>
                              {row.leaveType ?? row.leaveTypeId ?? "—"} · {formatLeaveDate(row.startDate)} →{" "}
                              {formatLeaveDate(row.endDate)}
                            </SoftTypography>
                          </SoftBox>
                          {String(row?.status ?? "Pending") === "Pending" &&
                          (row.id != null || row.Id != null) ? (
                            <FormControl
                              size="small"
                              sx={{ minWidth: 132, flexShrink: 0 }}
                              disabled={leaveUpdatingId === (row.id ?? row.Id)}
                            >
                              <Select
                                value={String(row.status ?? "Pending")}
                                displayEmpty
                                onChange={(e) => handleLeaveStatusChange(row, e.target.value)}
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "0.75rem",
                                  borderRadius: "10px",
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "rgba(118, 75, 162, 0.35)",
                                  },
                                }}
                              >
                                {LEAVE_STATUS_OPTIONS.map((opt) => (
                                  <MenuItem key={opt} value={opt}>
                                    {t(`status.${opt}`, opt)}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : (
                            <Chip
                              size="small"
                              label={t(`status.${row.status}`, row.status || "Pending")}
                              color={
                                String(row.status) === "Approved"
                                  ? "success"
                                  : String(row.status) === "Rejected"
                                    ? "error"
                                    : String(row.status) === "Pending"
                                      ? "warning"
                                      : "default"
                              }
                              variant={String(row.status) === "Pending" ? "filled" : "outlined"}
                              sx={{ fontWeight: 700, flexShrink: 0 }}
                            />
                          )}
                        </SoftBox>
                      ))}
                    </SoftBox>
                  )}
                </SoftBox>
              </Card>
            </Grid>
          </Grid>
        </SoftBox>}

        {SHOW_LICENSE_DASHBOARD_SECTIONS && !observerView && <SoftBox mb={3}>
          <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12} md={5}>
              <DefaultDoughnutChart
                title={t("dashboard.chart.quota_title", "Teacher licenses")}
                description={
                  <SoftTypography variant="body2" color="text.secondary" component="span">
                    {t(
                      "dashboard.chart.quota_summary_line",
                      "Licensed: {{licensed}} / {{max}} · Remaining licenses: {{remaining}} · Registered: {{registered}}"
                    )
                      .replace("{{licensed}}", String(dashboardStats.licensedTeachers))
                      .replace("{{max}}", String(dashboardStats.maxLicensedTeachers))
                      .replace("{{remaining}}", String(dashboardStats.remainingLicenses))
                      .replace("{{registered}}", String(dashboardStats.totalTeachers))}
                  </SoftTypography>
                }
                height="16rem"
                chart={teacherQuotaChart}
                showLegend
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <Card
                sx={{
                  height: "100%",
                  minHeight: 220,
                  borderRadius: "16px",
                  border: `1px solid ${figmaTokens.borderSubtle}`,
                  overflow: "hidden",
                  boxShadow: "0 14px 44px rgba(15, 23, 42, 0.08)",
                  bgcolor: figmaTokens.surface,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <SoftBox sx={{ height: 4, flexShrink: 0, background: figmaTokens.gradientDiagonalCSS }} />
                <SoftBox sx={{ p: { xs: 2, md: 2.25 }, flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                  <SoftBox>
                    <SoftTypography variant="subtitle1" fontWeight={800} sx={{ color: figmaTokens.textPrimary, letterSpacing: "-0.02em" }}>
                      {t("dashboard.chart.quota_insight_title", "Numbers at a glance")}
                    </SoftTypography>
                    <SoftTypography variant="caption" sx={{ color: figmaTokens.textSecondary, display: "block", mt: 0.75, lineHeight: 1.5 }}>
                      {t(
                        "dashboard.chart.quota_insight_subtitle",
                        "How many license slots are in use right now."
                      )}
                    </SoftTypography>
                  </SoftBox>

                  <SoftBox>
                    <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 0.75 }}>
                      <SoftTypography variant="caption" fontWeight={700} sx={{ color: figmaTokens.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                        {t("dashboard.chart.license_fill_label", "License utilization")}
                      </SoftTypography>
                      <SoftTypography variant="caption" fontWeight={800} sx={{ color: figmaTokens.rosterChartFilled }}>
                        {licenseUtilizationPct}%
                      </SoftTypography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={licenseUtilizationPct}
                      sx={{
                        height: 10,
                        borderRadius: 999,
                        bgcolor: "rgba(45, 212, 191, 0.22)",
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 999,
                          background: `linear-gradient(90deg, ${figmaTokens.rosterChartFilled} 0%, ${figmaTokens.rosterChartCapacity} 100%)`,
                        },
                      }}
                    />
                  </SoftBox>

                  <Stack spacing={1.25} sx={{ flex: 1 }}>
                    {[
                      {
                        label: t("dashboard.stats.licensed_teachers", "Licensed"),
                        value:
                          dashboardStats.maxLicensedTeachers > 0
                            ? `${dashboardStats.licensedTeachers} / ${dashboardStats.maxLicensedTeachers}`
                            : dashboardStats.licensedTeachers,
                        icon: "verified",
                        bar: figmaTokens.rosterChartFilled,
                        iconBg: "rgba(102, 126, 234, 0.14)",
                        iconColor: figmaTokens.rosterChartFilled,
                      },
                      {
                        label: t("dashboard.stats.remaining_licenses", "Remaining licenses"),
                        value: dashboardStats.remainingLicenses,
                        icon: "pie_chart",
                        bar:
                          dashboardStats.remainingLicenses > 0
                            ? figmaTokens.rosterChartOpen
                            : figmaTokens.rosterChartMuted,
                        iconBg:
                          dashboardStats.remainingLicenses > 0
                            ? "rgba(45, 212, 191, 0.16)"
                            : "rgba(148, 163, 184, 0.18)",
                        iconColor:
                          dashboardStats.remainingLicenses > 0
                            ? figmaTokens.rosterChartOpen
                            : figmaTokens.rosterChartMuted,
                      },
                      {
                        label: t("dashboard.stats.registered_teachers", "Registered teachers"),
                        value: dashboardStats.totalTeachers,
                        icon: "groups",
                        bar: figmaTokens.rosterChartCapacity,
                        iconBg: "rgba(118, 75, 162, 0.14)",
                        iconColor: figmaTokens.rosterChartCapacity,
                      },
                    ].map((row) => (
                      <SoftBox
                        key={row.label}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 2,
                          py: 1.35,
                          px: 1.5,
                          borderRadius: "14px",
                          border: `1px solid ${figmaTokens.borderSubtle}`,
                          background: `linear-gradient(120deg, ${row.iconBg} 0%, ${figmaTokens.pageBg} 52%)`,
                          boxShadow: "0 4px 18px rgba(15, 23, 42, 0.04)",
                          position: "relative",
                          overflow: "hidden",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: 4,
                            background: row.bar,
                          },
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0, pl: 0.25 }}>
                          <SoftBox
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: "12px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              bgcolor: row.iconBg,
                              border: `1px solid rgba(15, 23, 42, 0.06)`,
                            }}
                          >
                            <Icon sx={{ color: row.iconColor, fontSize: 20 }}>{row.icon}</Icon>
                          </SoftBox>
                          <SoftTypography variant="body2" fontWeight={700} sx={{ color: figmaTokens.textBody }}>
                            {row.label}
                          </SoftTypography>
                        </Stack>
                        <SoftTypography variant="h5" fontWeight={800} sx={{ color: figmaTokens.textPrimary, flexShrink: 0 }}>
                          {row.value}
                        </SoftTypography>
                      </SoftBox>
                    ))}
                  </Stack>
                </SoftBox>
              </Card>
            </Grid>
          </Grid>
        </SoftBox>}
      </SoftBox>
      {SHOW_RECRUITER_PREVIEW && !observerView && <RecruiterTeachersDialog
        open={recruiterTeachersOpen}
        onClose={() => {
          setRecruiterTeachersOpen(false);
          setRecruiterTeachersRow(null);
        }}
        recruiter={recruiterTeachersRow}
      />}
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
